import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Header from '../../components/ui/Header';

const Register = () => {
  const navigate = useNavigate()
  const { signUp, user, loading } = useAuth()
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    position: 'atacante'
  })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  // Redirect if already logged in
  useEffect(() => {
    if (user && !loading) {
      navigate('/dashboard')
    }
  }, [user, loading, navigate])

  const handleChange = (e) => {
    const { name, value } = e?.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const validateForm = () => {
    if (formData?.password !== formData?.confirmPassword) {
      setError('As senhas não coincidem')
      return false
    }
    if (formData?.password?.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres')
      return false
    }
    if (!formData?.name?.trim()) {
      setError('Nome é obrigatório')
      return false
    }
    return true
  }

  const handleSubmit = async (e) => {
    e?.preventDefault()
    setIsLoading(true)
    setError('')
    setSuccess('')

    if (!validateForm()) {
      setIsLoading(false)
      return
    }

    try {
      const { error: signUpError } = await signUp(
        formData?.email, 
        formData?.password, 
        {
          full_name: formData?.name,
          phone: formData?.phone,
          position: formData?.position
        }
      )
      
      if (signUpError) {
        setError(signUpError?.message || 'Erro ao criar conta')
      } else {
        setSuccess('Conta criada com sucesso! Verifique seu email para confirmar.')
      }
    } catch (err) {
      setError('Erro inesperado. Tente novamente.')
    } finally {
      setIsLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900">
              Criar nova conta
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Ou{' '}
              <Link 
                to="/login" 
                className="font-medium text-green-600 hover:text-green-500"
              >
                entrar na sua conta existente
              </Link>
            </p>
          </div>

          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  Nome Completo
                </label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData?.name}
                  onChange={handleChange}
                  placeholder="Seu nome completo"
                  className="mt-1"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData?.email}
                  onChange={handleChange}
                  placeholder="seu@email.com"
                  className="mt-1"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                  Telefone (Opcional)
                </label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData?.phone}
                  onChange={handleChange}
                  placeholder="(11) 99999-9999"
                  className="mt-1"
                />
              </div>

              <div>
                <label htmlFor="position" className="block text-sm font-medium text-gray-700">
                  Posição Preferida
                </label>
                <select
                  id="position"
                  name="position"
                  value={formData?.position}
                  onChange={handleChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
                >
                  <option value="goleiro">Goleiro</option>
                  <option value="zagueiro">Zagueiro</option>
                  <option value="lateral">Lateral</option>
                  <option value="volante">Volante</option>
                  <option value="meia">Meia</option>
                  <option value="atacante">Atacante</option>
                </select>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Senha
                </label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  required
                  value={formData?.password}
                  onChange={handleChange}
                  placeholder="Mínimo 6 caracteres"
                  className="mt-1"
                />
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                  Confirmar Senha
                </label>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  required
                  value={formData?.confirmPassword}
                  onChange={handleChange}
                  placeholder="Confirme sua senha"
                  className="mt-1"
                />
              </div>
            </div>

            {error && (
              <div className="rounded-md bg-red-50 p-4">
                <div className="text-sm text-red-700">{error}</div>
              </div>
            )}

            {success && (
              <div className="rounded-md bg-green-50 p-4">
                <div className="text-sm text-green-700">{success}</div>
              </div>
            )}

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full"
            >
              {isLoading ? 'Criando conta...' : 'Criar conta'}
            </Button>
          </form>

          <div className="text-center">
            <p className="text-xs text-gray-500">
              Ao criar uma conta, você concorda com nossos{' '}
              <Link to="/terms" className="text-green-600 hover:text-green-500">
                Termos de Uso
              </Link>{' '}
              e{' '}
              <Link to="/privacy" className="text-green-600 hover:text-green-500">
                Política de Privacidade
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Register